/**
 * Module-level logout registry — lets non-React code (e.g. the axios
 * response interceptor) trigger a full app logout without needing hooks.
 *
 * The registered handler should:
 *   1. Clear React Query cache
 *   2. Clear local/session storage of app-owned keys
 *   3. Call Auth0 `logout({ logoutParams: { returnTo: ... } })`
 *
 * Auth0's logout navigates the browser, so any code after `invokeLogout()`
 * is NOT guaranteed to execute. Treat it as a one-way door.
 */

type LogoutHandler = () => void;

let handler: LogoutHandler | null = null;

/** Register the app's logout function. Call once at mount inside Providers. */
export const registerLogout = (fn: LogoutHandler): void => {
  handler = fn;
};

/** Invoke the registered logout. Returns true if a handler was set. */
export const invokeLogout = (): boolean => {
  if (!handler) return false;
  try {
    handler();
    return true;
  } catch {
    return false;
  }
};
